// Pyodide (WebAssembly Python) — standalone browser sandbox page.
// See /app/frontend/src/lib/pyodideRuntime.js for the shared loader.

import { useState } from 'react';
import { Play, CircleNotch, Warning, CheckCircle, ChartLine, Code } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { runPython, getPyodide } from '@/lib/pyodideRuntime';

const DEFAULT_CODE = `# Pyodide runs Python 3 in your browser — no server involved.
# pandas / matplotlib / numpy are pre-loaded.

import pandas as pd
import matplotlib.pyplot as plt

data = {
    'モジュール': ['技術日報', 'ナレッジ', 'お知らせ', 'プロジェクト', 'ダイジェスト'],
    '件数': [10, 694, 47, 19, 1],
}
df = pd.DataFrame(data)
print(df)

df.plot(x='モジュール', y='件数', kind='bar', color='#9333ea', legend=False, figsize=(8, 4))
plt.title('AI 検索インデックス — モジュール別件数')
plt.tight_layout()
plt.savefig('/tmp/chart.png', dpi=90)

print('\\n合計:', df['件数'].sum(), '件')
`;

export default function PyodideRunner() {
  const [ready, setReady] = useState(!!window.__pyodideReady);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [code, setCode] = useState(DEFAULT_CODE);
  const [output, setOutput] = useState('');
  const [chartUrl, setChartUrl] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    setRunning(true); setOutput(''); setChartUrl(null); setErr(null);
    setLoading(true); setLoadingMsg('起動中…');
    try {
      await getPyodide(setLoadingMsg);
      setReady(true); setLoadingMsg(''); setLoading(false);
      const r = await runPython(code);
      setOutput(r.output); setErr(r.error); setChartUrl(r.chart);
      window.__pyodideReady = true;
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setRunning(false); setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="pyodide-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
          <Code size={18} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-zinc-900"
            data-testid="settings-page-title">コードインタープリタ</h1>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            ブラウザ内で Python 3 を実行します（pandas / matplotlib / numpy 対応・完全サンドボックス）。AI が生成した集計・可視化スクリプトをその場で走らせるための実験環境です。
          </p>
        </div>
        <div className={`text-[11px] px-2 py-1 rounded-full font-semibold ${
          ready ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-zinc-100 text-zinc-700 border border-zinc-200'}`}>
          {ready ? 'ランタイム準備完了' : '未初期化'}
        </div>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="rounded-lg border border-zinc-200 overflow-hidden bg-zinc-950">
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full bg-zinc-950 text-emerald-200 font-mono text-[12px] leading-relaxed px-3 py-2.5 focus:outline-none resize-y min-h-[220px]"
            spellCheck={false}
            data-testid="pyodide-code"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={run} disabled={running || loading}
            className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="pyodide-run-btn">
            {running ? <><CircleNotch size={13} className="animate-spin mr-1" />実行中…</>
              : loading ? <><CircleNotch size={13} className="animate-spin mr-1" />読み込み中…</>
              : <><Play size={13} weight="fill" className="mr-1" /> {ready ? 'コードを実行' : 'ランタイムを読み込んで実行'}</>}
          </Button>
          {loadingMsg && <p className="text-[11px] text-zinc-500" data-testid="pyodide-loading-msg">{loadingMsg}</p>}
        </div>
        {(output || err) && (
          <div className="rounded-lg border border-zinc-200 bg-zinc-50" data-testid="pyodide-output-panel">
            <div className="px-3 py-1.5 border-b border-zinc-200 flex items-center gap-2">
              {err ? <Warning size={13} className="text-rose-600" weight="fill" />
                   : <CheckCircle size={13} className="text-emerald-600" weight="fill" />}
              <p className="text-[11px] font-semibold text-zinc-700">{err ? '実行エラー' : '実行結果'}</p>
            </div>
            {output && <pre className="px-3 py-2 text-[12px] font-mono text-zinc-800 whitespace-pre-wrap max-h-[240px] overflow-y-auto" data-testid="pyodide-stdout">{output}</pre>}
            {err && <pre className="px-3 py-2 text-[12px] font-mono text-rose-700 whitespace-pre-wrap max-h-[240px] overflow-y-auto" data-testid="pyodide-stderr">{err}</pre>}
          </div>
        )}
        {chartUrl && (
          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="pyodide-chart-panel">
            <div className="px-3 py-1.5 border-b border-zinc-200 flex items-center gap-2">
              <ChartLine size={13} className="text-violet-600" weight="fill" />
              <p className="text-[11px] font-semibold text-zinc-700">生成されたグラフ</p>
            </div>
            <img src={chartUrl} alt="chart" className="w-full" data-testid="pyodide-chart-img" />
          </div>
        )}
        <p className="text-[11px] text-zinc-400">
          コードは全てブラウザ内で動作します（サーバーには送信されません）。ネットワーク・ファイルシステム・OS への直接アクセスは不可能です。
        </p>
      </div>
    </div>
  );
}
